import React from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import ImageComponent from 'shared/ImageComponent';
import { dollarFormat, getTenantDetails } from 'utils/utils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import GlobalTooltip from 'components/GlobalTooltip';

/**
 * Activity column body renderer (for automated time entries)
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Activity cell
 */
export const activityBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Activity</span>
        <span>-</span>
      </React.Fragment>
    );
  }

  // Normalize module_name: replace hyphens with spaces and ensure proper formatting
  // Handles formats like: "task-activity", "Task Activity", "Created Task", etc.
  const normalizeModuleName = (moduleName) => {
    if (!moduleName) return '-';
    // Replace hyphens with spaces
    const withSpaces = moduleName.replace(/-/g, ' ');
    return withSpaces;
  };

  const displayText = normalizeModuleName(rowData?.module_name);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">Activity</span>
      <span className="text-break capitalize-text" title={displayText}>
        {displayText}
      </span>
    </React.Fragment>
  );
};

/**
 * Billing Code column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Billing Code cell
 */
export const billingCodeBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Billing Code</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const displayText = rowData?.activity_type_code ? `${rowData?.activity_type} - ${rowData?.activity_type_code}` : rowData?.activity_type;
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Billing Code</span>
      <span className="text-break" title={displayText}>
        {displayText || '-'}
      </span>
    </React.Fragment>
  );
};

/**
 * Team Member column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Team Member cell
 */
export const teamMemberBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Team Member</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const [tenantId] = getTenantDetails();

  // Use new API structure: team_member_firm_user_id and team_member_user_name
  const firmUserId = rowData?.team_member_firm_user_id;
  const name = rowData?.team_member_user_name || '';

  // Extract first name for ImageComponent (use first word of team_member_user_name)
  const firstName = rowData?.team_member_user_name?.split(' ')[0] || '';

  return (
    <Link
      to={firmUserId ? `/settings/team-management/${firmUserId}` : '/settings/team-management'}
      className="redirection-link-no-text-transform"
    >
      <span className="p-column-title text-break">Team Member</span>
      <div className="d-flex align-items-center">
        <ImageComponent
          filePath={`${tenantId}/${firmUserId}/profile_image`}
          fileName={firstName}
          fileSize="small.jpg"
          style={{ borderRadius: '50% !important' }}
        />
        {firmUserId && <i className="fas fa-user-tie me-2"></i>}
        <span
          className="redirection-link-no-text-transform"
          title={name}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100px',
          }}
        >
          {name || '-'}
        </span>
      </div>
    </Link>
  );
};

/**
 * Description column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Description cell
 */
export const descriptionBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Description</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Description</span>
      <span className="text-break" title={rowData?.notes ? rowData?.notes : undefined}>
        {rowData?.notes ? rowData?.notes : '-'}
      </span>
    </React.Fragment>
  );
};

/**
 * Status column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Status cell
 */
export const statusBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  let status = '';
  if (rowData?.time_entry_status === 'OPEN') {
    status = 'Uninvoiced';
  } else {
    status = 'Invoiced';
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Status</span>
      <span className="text-break" title={status}>
        {status}
      </span>
    </React.Fragment>
  );
};

/**
 * Case/Lead column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {boolean} params.is_automated - Whether it's automated time entries
 * @returns {JSX.Element} - Case/Lead cell
 */
export const caseBody = (params) => {
  const { rowData, is_automated } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">{is_automated ? 'Case' : 'Case/Lead'}</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">{is_automated ? 'Case' : 'Case/Lead'}</span>
      <Link
        to={`${rowData?.is_lead ? '/leads' : '/cases'}/${rowData?.case_id}`}
        className="redirection-link-no-text-transform"
        title={rowData?.case_name?.replace(/-/g, ' ')}
      >
        {rowData?.case_name?.replace(/-/g, ' ') || '-'}
      </Link>
    </React.Fragment>
  );
};

/**
 * Duration column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Duration cell
 */
export const durationBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Duration</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Duration</span>
      <span className="text-break" title={rowData?.total_hours ? rowData?.total_hours : 0}>
        {rowData?.total_hours ? rowData?.total_hours : 0}
      </span>
    </React.Fragment>
  );
};

/**
 * Date column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {string} params.timezone - User timezone
 * @returns {JSX.Element} - Date cell
 */
export const dateBody = (params) => {
  const { rowData, timezone } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date</span>
        <span className="text-break">-</span>
      </React.Fragment>
    );
  }
  const formattedDate = convertDateFromApiToUserTimezone(rowData?.time_entry_date, timezone, constants?.month_date_year_format);
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Date</span>
      {rowData?.time_entry_date ? (
        <span className="text-break" title={formattedDate !== '-' ? formattedDate : undefined}>
          {formattedDate}
        </span>
      ) : (
        <span className="text-break">-</span>
      )}
    </React.Fragment>
  );
};

/**
 * Rate column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Rate cell
 */
export const rateBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Rate</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Rate</span>
      <span className="text-break" title={dollarFormat(rowData?.hour_rate)}>
        {dollarFormat(rowData?.hour_rate)}
      </span>
    </React.Fragment>
  );
};

/**
 * Amount column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @returns {JSX.Element} - Amount cell
 */
export const amountBody = (params) => {
  const { rowData } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Amount</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const getTotalAmount = (data) => {
    try {
      return dollarFormat(data);
    } catch {
      return `$0.00`;
    }
  };
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Amount</span>
      <span className="text-break" title={getTotalAmount(rowData?.amount)}>
        {getTotalAmount(rowData?.amount)}
      </span>
    </React.Fragment>
  );
};

/**
 * Billable checkbox column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onUpdateBillingStatus - Callback to update billing status
 * @returns {JSX.Element} - Billable cell
 */
export const billableBody = (params) => {
  const { rowData, onUpdateBillingStatus } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Billable</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const isInvoiced = rowData?.time_entry_status?.trim() === 'INVOICED';
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Billable</span>
      <label className={`switch ${isInvoiced ? 'opacity-50' : ''}`}>
        <input
          type="checkbox"
          checked={rowData.billable}
          name="billable"
          onChange={(e) => onUpdateBillingStatus && onUpdateBillingStatus(e, rowData)}
          disabled={isInvoiced}
        />
        <span className="slider round"></span>
      </label>
    </React.Fragment>
  );
};

/**
 * Actions column body renderer
 * @param {Object} params - Parameters object
 * @param {Object} params.rowData - Row data
 * @param {Function} params.onEdit - Callback to edit time entry
 * @param {Function} params.onDelete - Callback to delete time entry
 * @returns {JSX.Element} - Actions cell
 */
export const actionsBody = (params) => {
  const { rowData, onEdit, onDelete } = params || {};
  if (!rowData) {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Action</span>
        <span>-</span>
      </React.Fragment>
    );
  }
  const isInvoiced = rowData?.time_entry_status?.trim() === 'INVOICED';
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Action</span>
      <span role="button">
        <GlobalTooltip tooltip="Edit" className="ms-2">
          <i
            className={`icon-edit ${isInvoiced ? 'icon-disable' : ''}`}
            onClick={() => {
              if (!isInvoiced && onEdit) {
                onEdit(rowData);
              }
            }}
            id="edit"
          />
        </GlobalTooltip>
      </span>
      <span role="button">
        <GlobalTooltip tooltip="Delete" className="ms-4">
          <i
            className={`icon-delete ${isInvoiced ? 'icon-disable' : ''}`}
            onClick={() => {
              if (!isInvoiced && onDelete) {
                onDelete(rowData);
              }
            }}
            id="delete"
          />
        </GlobalTooltip>
      </span>
    </React.Fragment>
  );
};
